import { styled, useTheme } from '@mui/material/styles';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import { IconButton, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../../../constants/routes';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { AUTH_API } from '../../../api/amplify';
import { useState } from 'react';
import SelectedMenu from './SelectedMenu/Index';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MenuIcon from '@mui/icons-material/Menu';
import { ReactComponent as CopyMonkeyLogo } from '../../../assets/images/logo.svg';
import { ElamaIcon } from './styled';

const drawerWidth = 240;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  // zIndex: theme.zIndex.drawer + 1,
  // width: '500px',
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    // marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  background: 'transparent',
  height: '64px',
}));

interface IHeaderProps {
  onToggle: () => void;
  open: boolean;
}

const Header: React.FC<IHeaderProps> = ({ open, onToggle }) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { user } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const creditsLeft = user ? user?.credits.total - user?.credits.used : 0;
  const freeAvailableGenerations = user?.free_tier
    ? user.free_tier.total_generations - user.free_tier.used_generations
    : 0;
  // const plan = user?.subscriptions || 'Бесплатно';
  const isElamaProvider = user?.provider === 'eLama';

  const menuOptions = [
    // {
    //   value: 'billing',
    //   text: 'Тарифы',
    //   icon: <AttachMoneyIcon />,
    //   action: () => navigate(PATHS.billing),
    // },
    {
      value: 'logout',
      text: 'Выйти',
      icon: <LogoutRoundedIcon />,
      action: () => handleSignOut(),
    },
  ];

  const handleBillingCLick = (e: any) => {
    navigate(PATHS.billing);
  };

  const handleOpenMenu = (e: any) => {
    setIsOpen(true);
    setAnchorEl(e.currentTarget);
  };

  const handleCloseMenu = () => {
    setIsOpen(false);
    setAnchorEl(null);
  };

  const handleClickMenuItem = (action: () => void) => {
    action();
    handleCloseMenu();
  };

  const handleSignOut = async () => {
    try {
      await AUTH_API.signOut();
      navigate(PATHS.auth, { replace: true });
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <AppBar open={open} elevation={0} position={'absolute'}>
      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        alignItems={'center'}
        sx={{
          display: {
            mobile: 'flex',
            tablet: 'none',
            laptop: 'none',
          },
          p: '10px',
          alignItems: 'flex-end',
          background: theme.custom.palette.primary.white,
        }}
      >
        <CopyMonkeyLogo />
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
          sx={{
            cursor: 'pointer',
          }}
          onClick={handleOpenMenu}
        >
          <Typography
            sx={{
              color: theme.custom.palette.primary.black,
              fontWeight: 300,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '220px',
              alignSelf: 'center',
              p: '10px',
            }}
          >
            {user?.email}
          </Typography>
          <KeyboardArrowDownOutlinedIcon color={'primary'} fontSize={'small'} />
        </Stack>
        <IconButton onClick={onToggle}>
          <MenuIcon
            sx={{
              color: theme.custom.palette.primary.default,
            }}
          />
        </IconButton>
      </Stack>

      <Stack
        sx={{
          background: theme.custom.palette.primary.white,
          borderBottomLeftRadius: '60px',
          borderBottomRightRadius: '60px',
          padding: '11px 20px',
          alignSelf: 'center',
          boxShadow: 'rgb(34 60 80 / 13%) 0px 5px 10px 2px',
          display: { mobile: 'none', tablet: 'flex' },
          marginLeft: '56px',
          boxSizing: 'border-box',
        }}
        direction={'row'}
        gap={'20px'}
        justifyContent={'center'}
        alignItems={'center'}
      >
        <Stack
          direction={'row'}
          alignItems={'center'}
          gap={'5px'}
          sx={{ cursor: isElamaProvider ? 'default' : 'pointer' }}
          onClick={isElamaProvider ? undefined : handleBillingCLick}
        >
          {isElamaProvider ? (
            <>
              <ElamaIcon />
              <Typography sx={{ color: theme.custom.palette.primary.black, fontWeight: 600 }}>Тариф eLama</Typography>
            </>
          ) : (
            <>
              <PaymentsIcon sx={{ color: theme.custom.palette.primary.default }} fontSize={'small'} />
              <Typography sx={{ color: theme.custom.palette.primary.black, fontWeight: 600 }}>
                Купить генерации
              </Typography>
            </>
          )}
        </Stack>

        <Stack direction={'row'} alignItems={'center'} gap={'5px'} sx={{ cursor: 'default' }}>
          <AccountBalanceIcon
            sx={{ color: theme.custom.palette.primary.default, fontSize: '21px' }}
            fontSize={'small'}
          />
          <Typography sx={{ color: theme.custom.palette.primary.black, fontWeight: 600 }}>
            {creditsLeft <= 0 ? 'Бесплатных генераций: ' + freeAvailableGenerations : 'Генераций: ' + creditsLeft}
          </Typography>
        </Stack>

        <Stack direction={'row'} alignItems={'center'} gap={'5px'} sx={{ cursor: 'pointer' }} onClick={handleOpenMenu}>
          <AccountCircleRoundedIcon sx={{ color: theme.custom.palette.primary.default }} fontSize={'small'} />

          <Typography
            sx={{
              color: theme.custom.palette.primary.black,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '220px',
            }}
          >
            {user?.email}
          </Typography>
          <KeyboardArrowDownOutlinedIcon color={'primary'} fontSize={'small'} />
        </Stack>
      </Stack>

      <SelectedMenu
        isOpen={isOpen}
        handleCloseMenu={handleCloseMenu}
        handleClick={handleClickMenuItem}
        anchorEl={anchorEl}
        options={menuOptions}
      />
    </AppBar>
  );
};

export default Header;
